import { useState } from "react";
import { Link, Outlet, useRouterState } from "@tanstack/react-router";

import { ThemeToggle } from "@/components/theme-toggle";
import { Footer } from "@/components/footer";
import { PenStatusBadge } from "@/components/pen-status-badge";
import { UserMenu } from "@/components/user-menu";
import { Button } from "@/components/ui/button";
import { isLoggedIn } from "@/lib/session";

// Navigation entries. The chemistry-profile tab lands on the water-sources
// sub-route by default; child links surface inside that page.
const NAV = [
  { to: "/", label: "Dashboard" },
  { to: "/grows", label: "Grows" },
  { to: "/reservoirs", label: "Reservoirs" },
  { to: "/refills", label: "Refills" },
  { to: "/pending-actions", label: "Pending" },
  { to: "/chemistry-profile", label: "Chemistry" },
];

// RootLayout is the persistent app shell. Vertical nav on the left frees the
// header to host a glanceable pen-status strip (pH / EC / temp at a size you
// can read across the room). On screens < md the sidebar collapses to a
// drawer toggled from the header.
export function RootLayout() {
  const path = useRouterState({ select: (s) => s.location.pathname });
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Logged-out visitors only ever reach "/" (the server bounces every other
  // route to auth). Render the matched route — the public landing page — with
  // no app chrome; the landing supplies its own full-page layout.
  if (!isLoggedIn()) {
    return (
      <>
        <Outlet />
        {/* Corner version badge — fixed-positioned, so it shows on the
            logged-out landing too (matches taco/auth). */}
        <Footer />
      </>
    );
  }

  return (
    <div className="min-h-screen flex flex-col md:flex-row">
      {/* Sidebar (md+) */}
      <aside className="hidden md:flex md:w-56 md:flex-col md:border-r md:bg-muted/30">
        <SidebarBody path={path} />
      </aside>

      {/* Drawer (mobile) */}
      {drawerOpen && (
        <div
          className="fixed inset-0 z-40 md:hidden"
          role="dialog"
          aria-modal="true"
        >
          <div
            className="absolute inset-0 bg-background/80 backdrop-blur-sm"
            onClick={() => setDrawerOpen(false)}
          />
          <aside className="absolute inset-y-0 left-0 flex w-64 flex-col border-r bg-background shadow-lg">
            <SidebarBody
              path={path}
              onNavigate={() => setDrawerOpen(false)}
            />
          </aside>
        </div>
      )}

      <div className="flex flex-1 flex-col min-w-0">
        {/* Sticky so the pen-status strip (pH / EC / temp + connection) stays
            visible while scrolling a long grow/refill page. */}
        <header className="sticky top-0 z-30 border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/80">
          <div className="flex items-center justify-between gap-4 px-4 py-2 md:px-6">
            {/* Mobile nav toggle */}
            <Button
              variant="ghost"
              size="sm"
              className="md:hidden"
              onClick={() => setDrawerOpen(true)}
              aria-label="Open navigation"
            >
              ☰
            </Button>
            <div className="flex items-center gap-4 md:gap-6 min-w-0">
              <PenStatusBadge />
            </div>
            <div className="flex items-center gap-1">
              <UserMenu />
              <ThemeToggle />
            </div>
          </div>
        </header>
        <main className="flex-1">
          <div className="container mx-auto px-4 py-6 md:px-6 md:py-8">
            <Outlet />
          </div>
        </main>
        <Footer />
      </div>
    </div>
  );
}

function SidebarBody({
  path,
  onNavigate,
}: {
  path: string;
  onNavigate?: () => void;
}) {
  return (
    <>
      <div className="flex h-14 items-center border-b px-4">
        <Link
          to="/"
          onClick={onNavigate}
          className="text-lg font-semibold tracking-tight hover:opacity-80"
        >
          joesgrove
        </Link>
      </div>
      <nav className="flex-1 space-y-1 p-3">
        {NAV.map((n) => {
          const active = isActive(path, n.to);
          return (
            <Link
              key={n.to}
              to={n.to}
              onClick={onNavigate}
              className={
                "block rounded px-3 py-2 text-sm transition-colors " +
                (active
                  ? "bg-accent text-accent-foreground"
                  : "text-muted-foreground hover:text-foreground hover:bg-accent/50")
              }
            >
              {n.label}
            </Link>
          );
        })}
      </nav>
    </>
  );
}

// isActive marks a nav link as active when the current path equals or
// nests under it. "/" only matches exactly so it doesn't light up on
// every page.
function isActive(current: string, to: string): boolean {
  if (to === "/") return current === "/";
  return current === to || current.startsWith(to + "/");
}
